import { environment } from '@env/environment';
import { CbreLogger } from '@cbre/logger';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { prodDataDogTracings, qaDataDogTracings, uatDataDogTracings } from '@core/services/datadog-tracing-origins';

/**
 * DataDog-Rum Service factory for providers
 * @param dataDogInitializer
 * @returns
 */
export function dataDogFactory(cbreLogger: CbreLogger, userInfo: UserInfoResolver) {
  return () => new DatadogLoaderService(cbreLogger, userInfo).load();
}

/**
 * Service to load DataDog sdk @datadog/browser-rum initialize it with:
```ts
// Ideal implementation for Typescript Version +3.8.2
import { datadogRum } from '@datadog/browser-rum';

datadogRum.init({
    applicationId: APP_ID_TOKEN,
    clientToken: CLIENT_TOKEN,
    site: 'datadoghq.com',
    service:'market-iq',
    env:'qa',
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sampleRate: 100,
    trackInteractions: true,
    defaultPrivacyLevel: 'mask-user-input'
});
```
 */

export class DatadogLoaderService {
  #tracingOrigins = {
    prod: prodDataDogTracings,
    qa: qaDataDogTracings,
    uat: uatDataDogTracings,
  };
  constructor(
    private logger: CbreLogger,
    private userInfo: UserInfoResolver
  ) {}

  // APP_INITIALIZER must not await this: fire-and-forget so bootstrap never waits on the RUM chunk.
  load(): void {
    this.#load().catch(err => {
      this.logger.warn({
        eventName: 'APP_INITIALIZER',
        message: 'Datadog RUM failed to load',
        error: err,
      });
    });
  }

  async #load() {
    // check if DataDog is configured for the environment
    const dataDogEnv = environment['DataDogEnvironment'];
    if (dataDogEnv == null) {
      return null;
    }

    /*
     * Lazy-load the UMD bundle so it ships as its own chunk instead of statically in main.js.
     * It still attaches to window.DD_RUM as a side effect, so the API below is unchanged.
     */
    await import('@datadog/browser-rum/bundle/datadog-rum');

    const DdRumApi = (window as any).DD_RUM;

    // check if Datadog Rum SDK is available
    if (DdRumApi == null) {
      this.logger.warn({
        eventName: 'APP_INITIALIZER',
        message: `DataDog Browser SDK failed to load but should be enabled for ${dataDogEnv.env}`,
      });
      return null;
    }

    // Load DataDog initialization script
    const tracingOrigins = this.#tracingOrigins[dataDogEnv.env] ?? (() => [window.location.origin]);

    const ddOptions = {
      applicationId: dataDogEnv.APP_ID,
      // env variable/vault token provided at build stage
      clientToken: environment.DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      version: environment.appVersion,
      service: 'market-iq',
      env: dataDogEnv.env,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: tracingOrigins(),
      // Temporarily turning this off to prevent performance issues
      // sessionSampleRate: 100,
      // sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
    };

    DdRumApi.init(ddOptions);

    DdRumApi.setUser({
      email: this.userInfo?.resolve()?.emailAddress,
      id: this.userInfo?.resolve()?.id,
    });

    // Temporarily turning this off to prevent performance issues
    // DdRumApi.startSessionReplayRecording();
  }
}
